/**
 * Where everything is.
 *
 * The one screen that answers the question the whole system exists for. The
 * catalog says what a mould *is*; this says where it is, when it was last seen
 * and by whom - read off the scan ledger rather than a field anybody maintains.
 *
 * Online-only, and it says so. The ledger is not replicated to the device (it
 * grows without limit and a phone has no use for all of it), so this is the one
 * catalog screen that needs a connection. It keeps a cached copy so a dropped
 * connection shows stale data with its age rather than an empty page.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Download,
  MapPin,
  PackageSearch,
  RefreshCw,
  Search,
  TriangleAlert,
  X,
} from 'lucide-react';
import type { LiveBoardRow, MouldStatus } from '@mouldtrack/shared';
import { Page } from '../../components/layout/Page.js';
import { DataTable, type Column } from '../../components/data/DataTable.js';
import { Button } from '../../components/ui/button.js';
import { Input, Select } from '../../components/ui/field.js';
import { Badge, Code, EmptyState } from '../../components/ui/primitives.js';
import { api, isNetworkError } from '../../api/client.js';
import { daysSince, freshnessOf } from './freshness.js';

const CACHE_KEY = 'mt_tracking_board';

/** How long a tool can sit unscanned before it is worth pointing at. */
const STALE_DAYS = 30;

interface Cached {
  rows: LiveBoardRow[];
  fetchedAt: string;
}

const STATUS_TONE: Record<MouldStatus, 'success' | 'warning' | 'danger' | 'primary' | 'neutral'> = {
  IN_STORE: 'success',
  ISSUED: 'primary',
  IN_TRANSIT: 'warning',
  AT_VENDOR: 'warning',
  UNDER_MAINTENANCE: 'warning',
  SCRAPPED: 'danger',
};

const STATUS_LABEL: Record<MouldStatus, string> = {
  IN_STORE: 'In store',
  ISSUED: 'Issued',
  IN_TRANSIT: 'In transit',
  AT_VENDOR: 'At vendor',
  UNDER_MAINTENANCE: 'Maintenance',
  SCRAPPED: 'Scrapped',
};

function readCache(): Cached | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Cached) : null;
    return parsed && Array.isArray(parsed.rows) ? parsed : null;
  } catch {
    return null;
  }
}

/*
 * Freshness comes from the shared module now.
 *
 * This page had its own daysSince, its own wording and its own idea of stale,
 * while the catalog had none and the dashboard had a third. Two screens
 * disagreeing about whether a tool counts as stale is the kind of difference
 * nobody notices until they are comparing two numbers that should match.
 */

export function TrackingPage() {
  const [cached, setCached] = useState<Cached | null>(readCache);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Filters live in the URL, not in component state.
   *
   * Three things follow from that, and all three were missing. A filtered board
   * can be sent to someone - "the overdue ones at Vasai" is a link rather than
   * six instructions. Opening a mould and pressing back returns to the same
   * board instead of the unfiltered one, which is the difference between
   * working through a list and starting it again. And a refresh keeps the view.
   *
   * Replace rather than push, so that back leaves Tracking rather than walking
   * out through every keystroke of a search.
   */
  const [params, setParams] = useSearchParams();
  const param = <T extends string>(key: string) => (params.get(key) ?? '') as T;

  /**
   * Successive changes have to compose, and react-router's own updater will not.
   *
   * Its functional form reads a ref that is only refreshed on render, so two
   * changes made before React re-renders both start from the same value and the
   * first is silently dropped - clear one filter and set another quickly and
   * the cleared one comes back. Holding the pending value here makes each
   * change build on the last, and the effect below lets go once the URL has
   * caught up.
   */
  const pending = useRef<URLSearchParams | null>(null);
  useEffect(() => {
    pending.current = null;
  }, [params]);

  const write = (mutate: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(pending.current ?? params);
    mutate(next);
    pending.current = next;
    setParams(next, { replace: true });
  };

  const setParam = (key: string, value: string) => {
    write((next) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
  };

  /**
   * Exactly this filter and nothing else.
   *
   * What a summary tile has to mean. The tile counts the whole board, so if
   * clicking "Not seen in 30 days: 12" left another filter in place and showed
   * four rows, the number on the button would be contradicted by the result of
   * pressing it.
   */
  const only = (key: string, value: string) => {
    const next = new URLSearchParams();
    if (value) next.set(key, value);
    pending.current = next;
    setParams(next, { replace: true });
  };

  const search = param('q');
  const location = param('site');
  const store = param('store');
  const status = param('status');
  // Custody and staleness were the two questions the board could not answer
  // without reading every row: "what is out and late" and "what have we not
  // laid eyes on". Both are now one control each.
  const custody = param<'' | 'out' | 'in' | 'overdue'>('custody');
  const seen = param<'' | 'today' | 'week' | 'month' | 'stale' | 'never'>('seen');

  const setSearch = (v: string) => setParam('q', v);
  const setStore = (v: string) => setParam('store', v);
  const setStatus = (v: string) => setParam('status', v);
  const setCustody = (v: string) => setParam('custody', v);
  const setSeen = (v: string) => setParam('seen', v);
  // Changing site drops a store chosen under the previous one, which would
  // otherwise match nothing and read as "no moulds here".
  const setLocation = (v: string) => {
    write((next) => {
      if (v) next.set('site', v);
      else next.delete('site');
      next.delete('store');
    });
  };

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const rows = await api.liveBoard();
      const next: Cached = { rows, fetchedAt: new Date().toISOString() };
      setCached(next);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(next));
      } catch {
        // Quota or private mode: the in-memory copy still serves this session.
      }
    } catch (e) {
      setError(
        isNetworkError(e)
          ? 'No connection. Showing the last copy this device downloaded.'
          : 'Could not load the board.',
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    const onOnline = () => void load();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  const rows = cached?.rows;

  const locations = useMemo(() => {
    const names = new Set((rows ?? []).map((r) => r.location).filter(Boolean) as string[]);
    return [...names].sort().map((n) => ({ value: n, label: n }));
  }, [rows]);

  /**
   * Stores, narrowed to the chosen site.
   *
   * Every plant has a "Rack A1". Offering all of them at once produces a filter
   * that matches rows at three sites, which is a worse answer than none.
   */
  const stores = useMemo(() => {
    const names = new Set(
      (rows ?? [])
        .filter((r) => !location || r.location === location)
        .map((r) => r.store)
        .filter(Boolean) as string[],
    );
    return [...names].sort().map((n) => ({ value: n, label: n }));
  }, [rows, location]);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    const needle = search.trim().toLowerCase();
    const now = Date.now();
    return rows.filter((r) => {
      if (location && r.location !== location) return false;
      if (store && r.store !== store) return false;
      if (status && r.status !== status) return false;

      if (custody === 'out' && !r.issuedTo) return false;
      if (custody === 'in' && r.issuedTo) return false;
      if (custody === 'overdue') {
        if (!r.expectedBackBy || new Date(r.expectedBackBy).getTime() >= now) return false;
      }

      if (seen) {
        const days = daysSince(r.lastScannedAt);
        if (seen === 'never' && days !== null) return false;
        if (seen === 'today' && days !== 0) return false;
        if (seen === 'week' && (days === null || days > 7)) return false;
        if (seen === 'month' && (days === null || days > 30)) return false;
        if (seen === 'stale' && (days === null || days <= STALE_DAYS)) return false;
      }

      if (!needle) return true;
      // Who scanned it is searchable too - "everything R. Patil handled" is a
      // real question after a mis-scan, and it had no other way to be asked.
      return `${r.assetCode} ${r.name} ${r.location ?? ''} ${r.store ?? ''} ${r.checkpoint ?? ''} ${r.scannedBy ?? ''} ${r.issuedTo ?? ''}`
        .toLowerCase()
        .includes(needle);
    });
  }, [rows, search, location, store, status, custody, seen]);

  const activeFilters =
    (search ? 1 : 0) +
    (location ? 1 : 0) +
    (store ? 1 : 0) +
    (status ? 1 : 0) +
    (custody ? 1 : 0) +
    (seen ? 1 : 0);

  function clearAll() {
    only('', '');
  }

  /** The board as it is on screen, as a spreadsheet. */
  async function exportBoard() {
    const list = filtered ?? [];
    const header = [
      'Mould code',
      'Name',
      'Status',
      'Location',
      'Store',
      'Last checkpoint',
      'Scanned by',
      'Last seen',
      'Held by',
      'Due back',
      'Transfer',
    ];
    const body = list.map((r) => [
      r.assetCode,
      r.name,
      STATUS_LABEL[r.status],
      r.location ?? '',
      r.store ?? '',
      r.checkpoint ?? '',
      r.scannedBy ?? '',
      r.lastScannedAt ? new Date(r.lastScannedAt).toLocaleString() : 'never scanned',
      r.issuedTo ?? '',
      r.expectedBackBy ? new Date(r.expectedBackBy).toLocaleDateString() : '',
      r.activeTransferNo ?? '',
    ]);
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    wb.creator = 'MouldTrack';
    wb.created = new Date();
    const ws = wb.addWorksheet('Tracking');
    ws.addRow(header).font = { bold: true };
    for (const row of body) ws.addRow(row);
    ws.columns.forEach((c, i) => {
      c.width = Math.min(40, Math.max(12, header[i]!.length + 4));
    });
    const buf = await wb.xlsx.writeBuffer();
    const url = URL.createObjectURL(
      new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = `mouldtrack-tracking-${new Date().toISOString().slice(0, 10)}${activeFilters ? '-filtered' : ''}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /** The counts a manager actually looks at first. */
  const summary = useMemo(() => {
    const list = rows ?? [];
    const byLocation = new Map<string, number>();
    for (const r of list) {
      const key = r.location ?? 'Not yet scanned';
      byLocation.set(key, (byLocation.get(key) ?? 0) + 1);
    }
    return {
      total: list.length,
      neverScanned: list.filter((r) => !r.lastScannedAt).length,
      stale: list.filter((r) => (daysSince(r.lastScannedAt) ?? 0) > STALE_DAYS).length,
      checkedOut: list.filter((r) => r.issuedTo).length,
      overdue: list.filter(
        (r) => r.expectedBackBy && new Date(r.expectedBackBy).getTime() < Date.now(),
      ).length,
      byLocation: [...byLocation.entries()].sort((a, b) => b[1] - a[1]),
    };
  }, [rows]);

  const columns: Array<Column<LiveBoardRow>> = [
    {
      key: 'assetCode',
      header: 'Mould',
      primary: true,
      sortValue: (r) => r.assetCode,
      render: (r) => (
        <div className="flex min-w-0 items-center gap-2">
          <Code className="font-semibold">{r.assetCode}</Code>
          <span className="truncate">{r.name}</span>
        </div>
      ),
    },
    {
      key: 'location',
      header: 'Where it is',
      sortValue: (r) => r.location,
      render: (r) =>
        r.location ? (
          <span className="whitespace-nowrap">
            <MapPin className="mr-1 inline size-3.5 text-muted-foreground" aria-hidden />
            {r.location}
            {r.store ? <span className="text-muted-foreground"> · {r.store}</span> : null}
          </span>
        ) : (
          <span className="text-subtle-foreground">Not yet scanned</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (r) => r.status,
      render: (r) => <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>,
    },
    {
      key: 'issuedTo',
      header: 'Held by',
      sortValue: (r) => r.issuedTo ?? '',
      render: (r) => {
        if (!r.issuedTo) return <span className="text-subtle-foreground">-</span>;
        const due = r.expectedBackBy ? new Date(r.expectedBackBy).getTime() : null;
        const overdue = due !== null && due < Date.now();
        return (
          <span className={overdue ? 'text-danger' : undefined}>
            {r.issuedTo}
            {overdue ? ' · overdue' : ''}
          </span>
        );
      },
    },
    {
      key: 'checkpoint',
      header: 'Last checkpoint',
      hideOnMobile: true,
      sortValue: (r) => r.checkpoint,
      render: (r) =>
        r.checkpoint ? (
          <span className="whitespace-nowrap">
            {r.checkpoint}
            {r.scannedBy ? (
              <span className="text-muted-foreground"> · {r.scannedBy}</span>
            ) : null}
          </span>
        ) : (
          <span className="text-subtle-foreground">-</span>
        ),
    },
    {
      key: 'lastScannedAt',
      header: 'Last seen',
      hideOnMobile: true,
      // Sorted on the raw timestamp, not the words: "a month ago" and "2 days
      // ago" do not sort into any useful order alphabetically.
      sortValue: (r) => r.lastScannedAt ?? '',
      render: (r) => {
        const age = freshnessOf(r.lastScannedAt);
        // Only the two bands worth interrupting someone for get an icon. A
        // warning triangle beside every row a month old is wallpaper.
        const flag = age.band === 'cold' || age.band === 'never';
        return (
          <span
            className={age.tone}
            title={
              r.lastScannedAt
                ? `${new Date(r.lastScannedAt).toLocaleString()} - ${age.meaning}`
                : age.meaning
            }
          >
            {flag ? <TriangleAlert className="mr-1 inline size-3.5" aria-hidden /> : null}
            {age.band === 'never' ? 'never scanned' : age.label.replace(/^Seen /, '')}
          </span>
        );
      },
    },
    {
      key: 'transfer',
      header: 'Transfer',
      hideOnMobile: true,
      sortValue: (r) => r.activeTransferNo,
      render: (r) =>
        r.activeTransferNo ? (
          <Code className="text-xs">{r.activeTransferNo}</Code>
        ) : (
          <span className="text-subtle-foreground">-</span>
        ),
    },
  ];

  return (
    <Page
      title="Tracking"
      description={
        cached
          ? `${summary.total} moulds · updated ${freshnessOf(cached.fetchedAt).label.replace(/^Seen /, '')}`
          : 'Where every mould is, from the scan record.'
      }
      actions={
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => void exportBoard()}
            disabled={!filtered?.length}
          >
            <Download className="size-4" /> Export
          </Button>
          <Button variant="secondary" onClick={() => void load()} loading={loading}>
            <RefreshCw className="size-4" /> Refresh
          </Button>
        </div>
      }
      toolbar={
        // Two rows: what you are looking for, then how to narrow it. Six
        // controls on one line wraps into an unreadable block on a laptop.
        <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by ID, name or place…"
              className="pl-9"
              type="search"
              aria-label="Search the board"
            />
          </div>
          <Select
            aria-label="Filter by location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Everywhere"
            options={locations}
            className="sm:w-52"
          />
          <Select
            aria-label="Filter by status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            placeholder="Any status"
            options={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))}
            className="sm:w-44"
          />
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          {/* Only offered once a site is chosen, because every plant has a
              "Rack A1" and matching all of them at once is a worse answer
              than none. */}
          <Select
            aria-label="Filter by store"
            value={store}
            onChange={(e) => setStore(e.target.value)}
            placeholder={location ? 'Any store' : 'Any store (pick a site first)'}
            options={stores}
            disabled={stores.length === 0}
            className="sm:w-52"
          />
          <Select
            aria-label="Filter by custody"
            value={custody}
            onChange={(e) => setCustody(e.target.value)}
            placeholder="Anyone or nobody"
            options={[
              { value: 'out', label: 'Checked out' },
              { value: 'overdue', label: 'Checked out · overdue' },
              { value: 'in', label: 'Not checked out' },
            ]}
            className="sm:w-52"
          />
          <Select
            aria-label="Filter by when it was last seen"
            value={seen}
            onChange={(e) => setSeen(e.target.value)}
            placeholder="Seen at any time"
            options={[
              { value: 'today', label: 'Seen today' },
              { value: 'week', label: 'Seen in 7 days' },
              { value: 'month', label: 'Seen in 30 days' },
              { value: 'stale', label: `Not seen in ${STALE_DAYS} days` },
              { value: 'never', label: 'Never scanned' },
            ]}
            className="sm:w-52"
          />
          {activeFilters > 0 ? (
            <Button variant="ghost" onClick={clearAll} className="sm:w-auto">
              <X className="size-4" /> Clear {activeFilters}
            </Button>
          ) : null}
        </div>
        </div>
      }
    >
      {error ? (
        <p className="mb-3 rounded-lg bg-warning-subtle px-3 py-2 text-sm text-warning-foreground">
          {error}
        </p>
      ) : null}

      {rows?.length ? (
        <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Tile
            label="Moulds tracked"
            value={summary.total}
            active={activeFilters === 0}
            onClick={clearAll}
          />
          <Tile
            label="Checked out"
            value={summary.checkedOut}
            hint="Someone is holding these"
            active={custody === 'out'}
            onClick={() => only('custody', custody === 'out' ? '' : 'out')}
          />
          <Tile
            label="Overdue back"
            value={summary.overdue}
            tone={summary.overdue ? 'danger' : undefined}
            active={custody === 'overdue'}
            onClick={() => only('custody', custody === 'overdue' ? '' : 'overdue')}
          />
          <Tile
            label={`Not seen in ${STALE_DAYS} days`}
            value={summary.stale}
            tone={summary.stale ? 'warning' : undefined}
            hint={summary.neverScanned ? `${summary.neverScanned} never scanned` : undefined}
            active={seen === 'stale'}
            onClick={() => only('seen', seen === 'stale' ? '' : 'stale')}
          />
        </div>
      ) : null}

      {summary.byLocation.length > 1 ? (
        <div className="mb-4 flex flex-wrap gap-2">
          {summary.byLocation.map(([name, count]) => (
            <button
              key={name}
              type="button"
              onClick={() => setLocation(location === name ? '' : name === 'Not yet scanned' ? '' : name)}
              className={
                location === name
                  ? 'rounded-full border border-primary bg-primary-subtle px-3 py-1 text-sm'
                  : 'rounded-full border border-border px-3 py-1 text-sm hover:bg-surface-sunken'
              }
            >
              {name} <span className="text-muted-foreground">{count}</span>
            </button>
          ))}
        </div>
      ) : null}

      <DataTable
        rows={filtered}
        columns={columns}
        rowKey={(r) => r.assetCode}
        initialSort={{ key: 'lastScannedAt', direction: 'desc' }}
        empty={
          <EmptyState
            icon={<PackageSearch />}
            title={rows?.length ? 'Nothing matches' : 'Nothing scanned yet'}
            description={
              rows?.length
                ? 'Try a different search, or clear the filters.'
                : 'Once moulds are scanned on the floor, they appear here with where they were last seen.'
            }
            action={
              rows?.length ? (
                <Button variant="secondary" onClick={clearAll}>
                  Clear filters
                </Button>
              ) : (
                <Button variant="secondary" asChild>
                  <Link to="/moulds">Open the catalog</Link>
                </Button>
              )
            }
          />
        }
      />
    </Page>
  );
}

function Tile({
  label,
  value,
  tone,
  hint,
  active,
  onClick,
}: {
  label: string;
  value: number;
  tone?: 'warning' | 'danger';
  hint?: string;
  active?: boolean;
  onClick?: () => void;
}) {
  // A real button, not a clickable div: this is now the fastest way to filter
  // the board, so it has to be reachable by keyboard and announce itself.
  const Root = onClick ? 'button' : 'div';
  return (
    <Root
      {...(onClick ? { type: 'button' as const, onClick, 'aria-pressed': !!active } : {})}
      className={
        (onClick ? 'text-left transition hover:border-border-strong hover:bg-surface-sunken ' : '') +
        (active
          ? 'rounded-xl border border-primary bg-primary-subtle px-4 py-3'
          : 'rounded-xl border border-border bg-surface px-4 py-3')
      }
    >
      <div className="text-sm text-muted-foreground">{label}</div>
      <div
        className={
          tone === 'danger'
            ? 'text-2xl font-semibold text-danger'
            : tone === 'warning'
              ? 'text-2xl font-semibold text-warning-foreground'
              : 'text-2xl font-semibold'
        }
      >
        {value}
      </div>
      {hint ? <div className="text-xs text-subtle-foreground">{hint}</div> : null}
    </Root>
  );
}
